/**
 * Modal Component
 *
 * Reusable modal dialog component with backdrop, animations, and accessibility.
 * Follows design system from mockups with consistent styling.
 *
 * Usage:
 *   <Modal isOpen={isOpen} onClose={handleClose} title="Confirm Action">
 *     <Modal.Body>
 *       Are you sure you want to proceed?
 *     </Modal.Body>
 *     <Modal.Footer>
 *       <Button onClick={handleClose}>Cancel</Button>
 *       <Button variant="primary" onClick={handleConfirm}>Confirm</Button>
 *     </Modal.Footer>
 *   </Modal>
 */

import React, { useEffect, useRef } from 'react';
import { theme } from '@/styles/theme';

export interface ModalProps {
  /** Whether modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Modal title */
  title?: string;
  /** Modal size */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  /** Close button visible */
  showCloseButton?: boolean;
  /** Close on backdrop click */
  closeOnBackdrop?: boolean;
  /** Close on escape key */
  closeOnEscape?: boolean;
  /** Modal content */
  children: React.ReactNode;
  /** Additional classes */
  className?: string;
}

const ModalComponent: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  size = 'md',
  showCloseButton = true,
  closeOnBackdrop = true,
  closeOnEscape = true,
  children,
  className = '',
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle escape key
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Focus trap
  useEffect(() => {
    if (!isOpen) return;

    const modal = modalRef.current;
    if (!modal) return;

    const focusableElements = modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement?.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement?.focus();
          e.preventDefault();
        }
      }
    };

    modal.addEventListener('keydown', handleTab as any);
    firstElement?.focus();

    return () => {
      modal.removeEventListener('keydown', handleTab as any);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Size styles
  const sizeStyles = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    full: 'max-w-full mx-4',
  };

  // Backdrop styles
  const backdropStyles = `
    fixed inset-0 z-[1040]
    bg-black/50
    backdrop-blur-sm
    flex items-center justify-center
    p-4
    animate-fadeIn
  `;

  // Modal container styles
  const modalStyles = `
    relative w-full ${sizeStyles[size]}
    bg-card
    rounded-panel shadow-decide
    max-h-[90vh] overflow-y-auto
    animate-slideUp
  `;

  // Header styles
  const headerStyles = `
    flex items-center justify-between gap-4
    px-6 pt-6 pb-2
  `;

  // Close button styles
  const closeButtonStyles = `
    grid h-10 w-10 place-items-center rounded-btn
    text-ink-2 hover:text-ink
    hover:bg-inset focus:outline-none focus-visible:shadow-ring-ink
    transition-colors duration-200
  `;

  return (
    <div
      className={backdropStyles}
      onClick={closeOnBackdrop ? onClose : undefined}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div
        ref={modalRef}
        className={`${modalStyles} ${className}`.trim()}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || showCloseButton) && (
          <div className={headerStyles}>
            {title && (
              <h2
                id="modal-title"
                className="m-0 font-display text-[22px] font-extrabold tracking-[-0.02em] text-ink"
              >
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                onClick={onClose}
                className={closeButtonStyles}
                aria-label="Close modal"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        )}

        {children}
      </div>
    </div>
  );
};

// Modal Header subcomponent
export interface ModalHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

const ModalHeader: React.FC<ModalHeaderProps> = ({
  children,
  className = '',
  ...props
}) => {
  const headerStyles = `
    px-6 pt-6 pb-2
    [&_h2]:font-display [&_h2]:text-[22px] [&_h2]:font-extrabold [&_h2]:tracking-[-0.02em] [&_h2]:text-ink
    [&_h3]:font-display [&_h3]:text-[22px] [&_h3]:font-extrabold [&_h3]:tracking-[-0.02em] [&_h3]:text-ink
  `;

  return (
    <div className={`${headerStyles} ${className}`.trim()} {...props}>
      {children}
    </div>
  );
};

// Modal Body subcomponent
export interface ModalBodyProps extends React.HTMLAttributes<HTMLDivElement> {}

const ModalBody: React.FC<ModalBodyProps> = ({
  children,
  className = '',
  ...props
}) => {
  const bodyStyles = `
    px-6 py-5
  `;

  return (
    <div className={`${bodyStyles} ${className}`.trim()} {...props}>
      {children}
    </div>
  );
};

// Modal Footer subcomponent
export interface ModalFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Alignment of footer content */
  align?: 'left' | 'center' | 'right' | 'between';
}

const ModalFooter: React.FC<ModalFooterProps> = ({
  children,
  align = 'right',
  className = '',
  ...props
}) => {
  const alignStyles = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
    between: 'justify-between',
  };

  const footerStyles = `
    flex flex-wrap items-center gap-2.5
    px-6 pb-6 pt-4
    ${alignStyles[align]}
  `;

  return (
    <div className={`${footerStyles} ${className}`.trim()} {...props}>
      {children}
    </div>
  );
};

// Create Modal with subcomponents
const Modal = Object.assign(ModalComponent, {
  Header: ModalHeader,
  Body: ModalBody,
  Footer: ModalFooter,
});

// Export Modal
export { Modal };

// Export default
export default Modal;
